// Rótulos e regras da tela admin de NFS-e (WEB-125, `spec/28-nfse.md` §4.3, §4.6 e §5).

import type { AdminNfse, AdminNfseStatus, NfseSettings, NfseSettingsBody } from './queries.ts';

export const TABS = ['notas', 'configuracao', 'certificado'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABEL: Record<Tab, string> = {
  notas: 'Notas',
  configuracao: 'Configuração',
  certificado: 'Certificado',
};

export const STATUSES = [
  'pending',
  'issued',
  'rejected',
  'skipped',
  'cancelled',
  'credit',
] as const satisfies readonly AdminNfseStatus[];

type BadgeVariant = 'success' | 'destructive' | 'warning' | 'secondary';

export const STATUS: Record<AdminNfseStatus, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Pendente', variant: 'warning' },
  issued: { label: 'Emitida', variant: 'success' },
  rejected: { label: 'Rejeitada', variant: 'destructive' },
  skipped: { label: 'Ignorada', variant: 'secondary' },
  cancelled: { label: 'Cancelada', variant: 'destructive' },
  credit: { label: 'Crédito', variant: 'secondary' },
};

export function isStatus(value: string): value is AdminNfseStatus {
  return STATUSES.some((status) => status === value);
}

export function isTab(value: string): value is Tab {
  return TABS.some((tab) => tab === value);
}

/** Só `rejected`, `skipped` e `cancelled` reemitem (`spec/28` §5). */
export function canReissue(row: AdminNfse): boolean {
  return row.status === 'rejected' || row.status === 'skipped' || row.status === 'cancelled';
}

/** Só `issued` cancela (`spec/28` §4.6). */
export function canCancel(row: AdminNfse): boolean {
  return row.status === 'issued';
}

/** `xMotivo` do cancelamento: 15 a 255 caracteres (`cancelBodySchema`). */
export const CANCEL_REASON_MIN = 15;
export const CANCEL_REASON_MAX = 255;

/** Série e número da DPS, `-` enquanto a nota não tem número. */
export function dpsLabel(row: AdminNfse): string {
  return row.dps_series === null || row.dps_number === null
    ? '-'
    : `${row.dps_series}/${row.dps_number}`;
}

/** `12345678000190` vira `12.345.678/0001-90`. Fora de 14 dígitos volta como veio. */
export function formatCnpj(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) {
    return cnpj;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export const ENVIRONMENTS = [
  ['homologacao', 'Homologação'],
  ['producao', 'Produção'],
] as const;

/** `TSOpSimpNac` do layout nacional. */
export const SIMPLES_OP = [
  ['1', 'Não optante'],
  ['2', 'Optante MEI'],
  ['3', 'Optante ME ou EPP'],
] as const;

/** `TSRegApTribSN`. */
export const SIMPLES_REG_AP = [
  ['1', 'Federais e municipal pelo Simples Nacional'],
  ['2', 'Federais pelo Simples e municipal pela NFS-e'],
  ['3', 'Federais e municipal pela NFS-e'],
] as const;

/** `TSRegEspTrib`. */
export const REG_ESP_TRIB = [
  ['0', 'Nenhum'],
  ['1', 'Ato cooperado'],
  ['2', 'Estimativa'],
  ['3', 'Microempresa municipal'],
  ['4', 'Notário ou registrador'],
  ['5', 'Profissional autônomo'],
  ['6', 'Sociedade de profissionais'],
  ['9', 'Outros'],
] as const;

/** Acha o valor tipado da opção sem `as`: `Select` devolve `string`. */
export function pickOption<T extends string>(
  options: readonly (readonly [T, string])[],
  value: string,
): T | undefined {
  return options.find(([option]) => option === value)?.[0];
}

/** Campos do formulário "Configuração" como texto; `''` nos opcionais é `null` na API. */
export interface SettingsDraft {
  enabled: boolean;
  environment: NfseSettings['environment'];
  first_competence: string;
  series: string;
  ctrib_nac: string;
  lc116_item: string;
  service_description: string;
  municipal_inscription: string;
  simples_op_simp_nac: NfseSettings['simples_op_simp_nac'];
  simples_reg_ap_trib_sn: string;
  simples_reg_esp_trib: NfseSettings['simples_reg_esp_trib'];
  simples_p_tot_trib_sn: string;
  ver_aplic: string;
}

export function draftOf(settings: NfseSettings): SettingsDraft {
  return {
    enabled: settings.enabled,
    environment: settings.environment,
    first_competence: settings.first_competence,
    series: settings.series,
    ctrib_nac: settings.ctrib_nac ?? '',
    lc116_item: settings.lc116_item ?? '',
    service_description: settings.service_description,
    municipal_inscription: settings.municipal_inscription ?? '',
    simples_op_simp_nac: settings.simples_op_simp_nac,
    simples_reg_ap_trib_sn: settings.simples_reg_ap_trib_sn ?? '',
    simples_reg_esp_trib: settings.simples_reg_esp_trib,
    simples_p_tot_trib_sn: settings.simples_p_tot_trib_sn ?? '',
    ver_aplic: settings.ver_aplic,
  };
}

function orNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

/** O `PUT` é merge: só vai o que mudou (`null` limpa os opcionais). */
export function changesOf(
  current: NfseSettings,
  draft: SettingsDraft,
): Omit<NfseSettingsBody, 'reason'> {
  const body: Omit<NfseSettingsBody, 'reason'> = {};
  if (draft.enabled !== current.enabled) {
    body.enabled = draft.enabled;
  }
  if (draft.environment !== current.environment) {
    body.environment = draft.environment;
  }
  if (draft.first_competence !== current.first_competence) {
    body.first_competence = draft.first_competence;
  }
  if (draft.series.trim() !== current.series) {
    body.series = draft.series.trim();
  }
  if (orNull(draft.ctrib_nac) !== current.ctrib_nac) {
    body.ctrib_nac = orNull(draft.ctrib_nac);
  }
  if (orNull(draft.lc116_item) !== current.lc116_item) {
    body.lc116_item = orNull(draft.lc116_item);
  }
  if (draft.service_description.trim() !== current.service_description) {
    body.service_description = draft.service_description.trim();
  }
  if (orNull(draft.municipal_inscription) !== current.municipal_inscription) {
    body.municipal_inscription = orNull(draft.municipal_inscription);
  }
  if (draft.simples_op_simp_nac !== current.simples_op_simp_nac) {
    body.simples_op_simp_nac = draft.simples_op_simp_nac;
  }
  const regAp = pickOption(SIMPLES_REG_AP, draft.simples_reg_ap_trib_sn) ?? null;
  if (regAp !== current.simples_reg_ap_trib_sn) {
    body.simples_reg_ap_trib_sn = regAp;
  }
  if (draft.simples_reg_esp_trib !== current.simples_reg_esp_trib) {
    body.simples_reg_esp_trib = draft.simples_reg_esp_trib;
  }
  if (orNull(draft.simples_p_tot_trib_sn) !== current.simples_p_tot_trib_sn) {
    body.simples_p_tot_trib_sn = orNull(draft.simples_p_tot_trib_sn);
  }
  if (draft.ver_aplic.trim() !== current.ver_aplic) {
    body.ver_aplic = draft.ver_aplic.trim();
  }
  return body;
}

/** Nome do campo da API que o formulário recusa antes de enviar, ou `undefined`. */
export function invalidField(draft: SettingsDraft): string | undefined {
  const optional = (text: string, pattern: RegExp): boolean =>
    text.trim() === '' || pattern.test(text.trim());
  if (!/^\d{1,5}$/.test(draft.series.trim())) {
    return 'series';
  }
  if (!optional(draft.ctrib_nac, /^\d{6}$/)) {
    return 'ctrib_nac';
  }
  if (!optional(draft.simples_p_tot_trib_sn, /^\d{1,3}\.\d{2}$/)) {
    return 'simples_p_tot_trib_sn';
  }
  if (draft.service_description.trim() === '' || draft.service_description.length > 500) {
    return 'service_description';
  }
  const verAplic = draft.ver_aplic.trim();
  if (verAplic === '' || verAplic.length > 20) {
    return 'ver_aplic';
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(draft.first_competence)) {
    return 'first_competence';
  }
  return undefined;
}
